import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Lock, LockOpen, PackagePlus, ChevronDown, FileDown, FileSpreadsheet, Link2, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/Badges';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { downloadFile } from '../lib/download';
import { errMsg, fmtMoney } from '../lib/format';

const EditCell = ({ value, onCommit, disabled, testid, step = '0.01', className = '' }) => {
  const [val, setVal] = useState(value);
  React.useEffect(() => setVal(value), [value]);
  return (
    <Input
      type="number"
      step={step}
      value={val ?? ''}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (String(val) !== String(value)) onCommit(Number(val)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={`h-8 w-24 text-right font-mono text-[13px] ${className}`}
      data-testid={testid}
    />
  );
};

export default function ContenedorEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canDelete, canFinalize } = useAuth();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [selCatalog, setSelCatalog] = useState('');
  const [selCantidad, setSelCantidad] = useState(1);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ descripcion: '', cantidad: 1, precio_unitario: 0, descuento_pct: 0, moneda: 'MXN' });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const { data: cq, isLoading } = useQuery({
    queryKey: ['container-full', id],
    queryFn: async () => (await api.get(`/container-quotations/${id}/full`)).data,
  });

  const { data: catalogo } = useQuery({
    queryKey: ['catalog', 'containers', '', ''],
    queryFn: async () => (await api.get('/catalog/containers', { params: { limit: 200 } })).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['container-full', id] });
  const onError = (e) => toast.error(errMsg(e));

  const addFromCatalog = useMutation({
    mutationFn: async () => (await api.post('/container-items/from-catalog', { container_quotation_id: id, catalog_id: selCatalog, cantidad: Number(selCantidad) })).data,
    onSuccess: () => { invalidate(); setCatalogOpen(false); toast.success('Contenedor agregado'); },
    onError,
  });

  const addManual = useMutation({
    mutationFn: async () => (await api.post('/container-items', { container_quotation_id: id, ...manual, cantidad: Number(manual.cantidad), precio_unitario: Number(manual.precio_unitario), descuento_pct: Number(manual.descuento_pct) })).data,
    onSuccess: () => { invalidate(); setManualOpen(false); toast.success('Línea agregada'); },
    onError,
  });

  const updateItem = useMutation({
    mutationFn: async ({ itemId, patch }) => (await api.put(`/container-items/${itemId}`, patch)).data,
    onSuccess: invalidate,
    onError,
  });

  const deleteItem = useMutation({
    mutationFn: async (itemId) => (await api.delete(`/container-items/${itemId}`)).data,
    onSuccess: () => { invalidate(); toast.success('Línea eliminada'); },
    onError,
  });

  const finalizeMutation = useMutation({
    mutationFn: async () => (await api.post(`/container-quotations/${id}/finalize`)).data,
    onSuccess: () => { invalidate(); toast.success('Cotización finalizada'); },
    onError,
  });

  const reopenMutation = useMutation({
    mutationFn: async () => (await api.post(`/container-quotations/${id}/reopen`)).data,
    onSuccess: () => { invalidate(); toast.success('Cotización reabierta'); },
    onError,
  });

  const shareMutation = useMutation({
    mutationFn: async () => (await api.post(`/container-quotations/${id}/generate-share-link`)).data,
    onSuccess: async (d) => {
      const url = `${window.location.origin}/portal/${d.token}`;
      setShareUrl(url);
      setShareOpen(true);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Enlace copiado al portapapeles');
      } catch {
        toast.info('Enlace generado');
      }
    },
    onError,
  });

  const doDownload = async (url, filename, label) => {
    try {
      await downloadFile(url, filename);
      toast.success(`${label} descargado`);
    } catch (e) {
      toast.error(errMsg(e, `No se pudo descargar ${label}`));
    }
  };

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-12" /><Skeleton className="h-72" /></div>;
  if (!cq) return <div className="text-muted-foreground">Cotización no encontrada</div>;

  const frozen = cq.status === 'finalized';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contenedores')} data-testid="back-to-containers-button">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{cq.nombre}</h2>
              <StatusBadge status={cq.status} />
            </div>
            <p className="text-sm text-muted-foreground font-mono">{cq.quotation_number} · {cq.cliente_nombre}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="container-actions-menu">
                Acciones <ChevronDown size={14} className="ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => doDownload(`/container-quotations/${id}/export-pdf`, `${cq.quotation_number}.pdf`, 'PDF')} data-testid="container-export-pdf-button">
                <FileDown size={14} className="mr-2" /> Exportar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => doDownload(`/container-quotations/${id}/export-excel`, `${cq.quotation_number}.xlsx`, 'Excel')} data-testid="container-export-excel-button">
                <FileSpreadsheet size={14} className="mr-2" /> Exportar Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => shareMutation.mutate()} data-testid="container-share-link-button">
                <Link2 size={14} className="mr-2" /> Enlace para cliente
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {!frozen && (
            <>
              <Button variant="outline" onClick={() => { setSelCatalog(''); setSelCantidad(1); setCatalogOpen(true); }} data-testid="add-from-catalog-button">
                <PackagePlus size={15} className="mr-1.5" /> Del catálogo
              </Button>
              <Button variant="outline" onClick={() => setManualOpen(true)} data-testid="add-manual-item-button">
                <Plus size={15} className="mr-1.5" /> Línea manual
              </Button>
            </>
          )}
          {canFinalize && (frozen ? (
            <Button variant="outline" onClick={() => reopenMutation.mutate()} data-testid="reopen-container-button">
              <LockOpen size={15} className="mr-1.5" /> Reabrir
            </Button>
          ) : (
            <Button onClick={() => finalizeMutation.mutate()} data-testid="finalize-container-button">
              <Lock size={15} className="mr-1.5" /> Finalizar
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-border shadow-[var(--shadow-elev-1)]">
          <CardContent className="p-0">
            <Table data-testid="container-items-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Descripción</TableHead>
                  <TableHead className="th-label">Cantidad</TableHead>
                  <TableHead className="th-label text-right">Precio unit.</TableHead>
                  <TableHead className="th-label">Desc. %</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                  <TableHead className="th-label w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cq.items || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin líneas. Agrega contenedores del catálogo.</TableCell></TableRow>
                )}
                {(cq.items || []).map((it) => (
                  <TableRow key={it.id} className="hover:bg-background/40" data-testid={`container-item-row-${it.id}`}>
                    <TableCell>
                      <div className="font-medium text-[13px]">{it.descripcion}</div>
                      <div className="text-[11px] text-muted-foreground">{[it.tipo, it.condicion, it.medidas].filter(Boolean).join(' · ')}</div>
                    </TableCell>
                    <TableCell>
                      <EditCell value={it.cantidad} step="1" disabled={frozen} testid={`item-cantidad-${it.id}`} onCommit={(v) => updateItem.mutate({ itemId: it.id, patch: { cantidad: v } })} className="w-16" />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditCell value={it.precio_unitario} disabled={frozen} testid={`item-precio-${it.id}`} onCommit={(v) => updateItem.mutate({ itemId: it.id, patch: { precio_unitario: v } })} />
                    </TableCell>
                    <TableCell>
                      <EditCell value={it.descuento_pct} disabled={frozen} testid={`item-descuento-${it.id}`} onCommit={(v) => updateItem.mutate({ itemId: it.id, patch: { descuento_pct: v } })} className="w-16" />
                    </TableCell>
                    <TableCell className="money">{fmtMoney(it.total, it.moneda)}</TableCell>
                    <TableCell>
                      {!frozen && canDelete && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteItem.mutate(it.id)} data-testid={`delete-item-${it.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-border shadow-[var(--shadow-elev-1)] h-fit" data-testid="container-summary-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base">Resumen</CardTitle></CardHeader>
          <CardContent className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="money" data-testid="summary-subtotal">{fmtMoney(cq.totals?.subtotal, cq.moneda)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">IVA ({cq.iva_percentage}%)</span><span className="money" data-testid="summary-iva">{fmtMoney(cq.totals?.iva_amount, cq.moneda)}</span></div>
            <Separator />
            <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="money text-primary" data-testid="summary-total">{fmtMoney(cq.totals?.total, cq.moneda)}</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog: agregar desde catálogo */}
      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="max-w-md" data-testid="container-catalog-dialog">
          <DialogHeader><DialogTitle>Agregar del catálogo de contenedores</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Contenedor</Label>
              <Select value={selCatalog} onValueChange={setSelCatalog}>
                <SelectTrigger data-testid="catalog-container-select"><SelectValue placeholder="Selecciona un contenedor" /></SelectTrigger>
                <SelectContent>
                  {(catalogo?.items || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.descripcion} · {fmtMoney(c.precio_unitario, c.moneda)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cantidad</Label>
              <Input type="number" min="1" value={selCantidad} onChange={(e) => setSelCantidad(e.target.value)} data-testid="catalog-cantidad-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatalogOpen(false)}>Cancelar</Button>
            <Button onClick={() => addFromCatalog.mutate()} disabled={!selCatalog || addFromCatalog.isPending} data-testid="confirm-add-catalog-button">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: línea manual */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md" data-testid="container-manual-dialog">
          <DialogHeader><DialogTitle>Nueva línea manual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Descripción *</Label>
              <Input value={manual.descripcion} onChange={(e) => setManual((m) => ({ ...m, descripcion: e.target.value }))} data-testid="manual-descripcion-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Cantidad</Label><Input type="number" value={manual.cantidad} onChange={(e) => setManual((m) => ({ ...m, cantidad: e.target.value }))} data-testid="manual-cantidad-input" /></div>
              <div className="space-y-1.5"><Label>Precio unitario</Label><Input type="number" step="0.01" value={manual.precio_unitario} onChange={(e) => setManual((m) => ({ ...m, precio_unitario: e.target.value }))} data-testid="manual-precio-input" /></div>
              <div className="space-y-1.5"><Label>Descuento %</Label><Input type="number" value={manual.descuento_pct} onChange={(e) => setManual((m) => ({ ...m, descuento_pct: e.target.value }))} data-testid="manual-descuento-input" /></div>
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={manual.moneda} onValueChange={(v) => setManual((m) => ({ ...m, moneda: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button onClick={() => addManual.mutate()} disabled={!manual.descripcion || addManual.isPending} data-testid="confirm-add-manual-button">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: enlace compartido */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="container-share-dialog">
          <DialogHeader><DialogTitle>Enlace para cliente</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Cualquier persona con este enlace podrá ver la cotización en modo lectura.</p>
          <div className="flex gap-2">
            <Input readOnly value={shareUrl} className="font-mono text-[12px]" data-testid="container-share-url-input" />
            <Button
              variant="outline"
              size="icon"
              data-testid="container-copy-share-button"
              onClick={async () => {
                try { await navigator.clipboard.writeText(shareUrl); toast.success('Enlace copiado'); } catch { toast.error('No se pudo copiar'); }
              }}
            >
              <Copy size={15} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
